import { DocumentSubCategoryDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as DocumentSubCategoryActions from './document-sub-category.actions';

export const DOCUMENT_SUB_CATEGORY_FEATURE_KEY = 'documentsubcategory';

export interface DocumentSubCategoryState
  extends EntityState<DocumentSubCategoryDto> {
  selectedDocumentSubCategoryId?: string | number; // which DocumentSubCategory record has been selected
  loaded: boolean; // has the DocumentSubCategory list been loaded
  error?: string | null; // last none error (if any)
}

export interface DocumentSubCategoryPartialState {
  readonly [DOCUMENT_SUB_CATEGORY_FEATURE_KEY]: DocumentSubCategoryState;
}

export const documentSubCategoryAdapter: EntityAdapter<DocumentSubCategoryDto> = createEntityAdapter<
  DocumentSubCategoryDto
>({
  selectId: (documentSubCategory) => documentSubCategory.id,
  sortComparer: false,
});

export const initialState: DocumentSubCategoryState = documentSubCategoryAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedDocumentSubCategoryId: null,
  }
);

const documentSubCategoryReducer = createReducer(
  initialState,
  on(DocumentSubCategoryActions.loadDocumentSubCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    DocumentSubCategoryActions.loadDocumentSubCategorySuccess,
    (state, { documentSubCategory }) =>
      documentSubCategoryAdapter.addAll(documentSubCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(
    DocumentSubCategoryActions.loadDocumentSubCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(DocumentSubCategoryActions.addDocumentSubCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    DocumentSubCategoryActions.addDocumentSubCategorySuccess,
    (state, { newDocumentSubCategory }) =>
      documentSubCategoryAdapter.addMany(newDocumentSubCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(
    DocumentSubCategoryActions.addDocumentSubCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(DocumentSubCategoryActions.updateDocumentSubCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    DocumentSubCategoryActions.updateDocumentSubCategorySuccess,
    (state, { updateDocumentSubCategory }) =>
      documentSubCategoryAdapter.upsertMany(updateDocumentSubCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(
    DocumentSubCategoryActions.updateDocumentSubCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(DocumentSubCategoryActions.removeDocumentSubCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    DocumentSubCategoryActions.removeDocumentSubCategorySuccess,
    (state, { ids }) =>
      documentSubCategoryAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    DocumentSubCategoryActions.removeDocumentSubCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: DocumentSubCategoryState | undefined,
  action: Action
) {
  return documentSubCategoryReducer(state, action);
}
